import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid2 as Grid, Box, Typography } from '@mui/material';

// Components
import Copyright from '../internals/components/Copyright';
import DispatchSummary from './DispatchSummary';
import LoadingComponent from './LoadingComponent';
import StrategyEvaluationPanel from './StrategyEvaluationPanel';
import PageViewsBarChart from './PageViewsBarChart';
import SessionsChart from './SessionsChart';
import StatCard from './StatCard';
import AppCurrentSubject from './AppCurrentSubject';
import PagedCars from './PagedCars';

// Utils
import { getMinTime, cloneDatetTime } from '../utils/datetimeUtils';

// Actions & Selectors
import { selectTargetdemandData, selectTargetdemandStatus, fetchTargetdemandDataWithFilters } from '../../features/targetdemand/targetdemandSlice';
import { selectQueryDate } from '../../features/uidata/uidataSlice';
import { fetchCarInfoDetailsWithFilters, selectCarInfoDetailsinfoStatus, selectVehicles } from '../../features/carinfodetails/carinfodetailsSlice';
import { fetchAnalyticsAggregationData, selectAnalyticsData, selectAnalyticsStatus } from '../../features/analytics/analyticsAggregationSlice';

const REFRESH_INTERVAL = 120 * 1000;

export default function MainGrid() {
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);

  // --- 1. Optimized Selectors ---
  const targetdemandAPIData = useSelector(selectTargetdemandData);
  const targetdemandStatus = useSelector(selectTargetdemandStatus);
  const vehicles = useSelector(selectVehicles);
  const carStatus = useSelector(selectCarInfoDetailsinfoStatus);
  const analyticsStatus = useSelector(selectAnalyticsStatus);
  const analyticsData = useSelector(selectAnalyticsData) || {};
  const { beginingDate: rawBegin, endDate: rawEnd } = useSelector(selectQueryDate);

  // --- 2. Memoized Configuration ---
  const config = useMemo(() => {
    const start = new Date(rawBegin);
    const end = new Date(rawEnd);
    const now = new Date();
    let current = new Date();
    let annotation = true;

    if (start.getTime() > now.getTime()) {
      current = cloneDatetTime(start);
      annotation = false;
    } else if (end.getTime() < now.getTime()) {
      current = cloneDatetTime(end);
      annotation = false;
    }

    return {
      beginingDate: start,
      endDate: end,
      filterEndTime: getMinTime(end, now),
      lastDays: Math.floor(Math.abs(end - start) / 86400000),
      currentDateTime: current,
      showAnnotation: annotation
    };
  }, [rawBegin, rawEnd]);

  const getCustomGridSx = useCallback((width) => ({
    flex: `0 0 ${width}`,
    maxWidth: width,
    minWidth: 0,
  }), []);

  // --- 3. Non-Blocking Prioritized Data Fetching ---
  const refreshDashboardData = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    const fetchParams = {
      filters: {
        priceAgg: { start_time: { lte: config.beginingDate.getTime() }, end_time: { gte: config.endDate.getTime() } },
        buildingForecastAgg: { end_time: { gte: config.endDate.getTime() } },
        netLoadAgg: { end_time: { gte: config.endDate.getTime() } },
        smartMeterAgg: { time_count: { gte: Math.floor(config.beginingDate.getTime() / 1000), lte: Math.floor(config.filterEndTime / 1000) } },
        sumChargeAgg: { start_time: { gte: config.beginingDate.getTime() }, end_time: { lte: config.endDate.getTime() } },
        chargingInsightsAgg: { start_time: { gte: config.beginingDate, lte: config.filterEndTime } },
        summationsiteAgg: { timestamp: { gte: config.beginingDate, lte: config.filterEndTime } },
      }
    };

    dispatch(fetchCarInfoDetailsWithFilters({ signal }));

    setTimeout(() => {
      dispatch(fetchAnalyticsAggregationData(fetchParams));
      dispatch(fetchTargetdemandDataWithFilters({ signal }));
    }, 0);

  }, [dispatch, config]);

  // --- 4. Lifecycle & Polling ---
  useEffect(() => {
    refreshDashboardData();
    const timer = setInterval(refreshDashboardData, REFRESH_INTERVAL);
    return () => {
      clearInterval(timer);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [refreshDashboardData]);

  // --- 5. Data Extraction ---
  const targetdemand = targetdemandAPIData?.[0]?.targetdemand ?? 150;
  const { 
    summationsiteAgg, netLoadAgg, priceAgg, 
    smartMeterAgg, sumChargeAgg, chargingInsightsAgg, 
    buildingForecastAgg 
  } = analyticsData;

  const chargerBreakdownSeries = sumChargeAgg?.data?.charger_breakdown ?? [];

  return (
    <Box sx={{ width: '100%', maxWidth: '1700px', p: { xs: 1, md: 3 } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2, mt: -3 }}>Overview</Typography>
      
      <Grid container spacing={3} alignItems="stretch">
        
        {/* Vehicles Section (Independent Load) */}
        <Grid size={12}>
          {/* Priority: Show vehicles if they exist; otherwise show loading only if actually fetching */}
          {vehicles && vehicles.length > 0 ? (
            <PagedCars carData={vehicles} />
          ) : carStatus === 'loading' ? (
            <Box sx={{ py: 4 }}>
              <LoadingComponent />
            </Box>
          ) : carStatus === 'failed' ? (
            <Typography color="error" align="center">Error loading vehicles</Typography>
          ) : null}
        </Grid>

        {/* Analytics Summary Section */}
        <Grid size={12}>
          {/* Only show loader if we have NO data and are currently loading */}
          {analyticsStatus === 'loading' && !summationsiteAgg ? (
            <Box sx={{ py: 4 }}>
              {/* <LoadingComponent /> */}
              </Box>
          ) : (
            <DispatchSummary
              {...config}
              targetdemand={targetdemand}
              disChargeMin={summationsiteAgg?.data?.disChargeMin ?? 0}
              charges={summationsiteAgg?.data?.charges ?? []}
              discharges={summationsiteAgg?.data?.discharges ?? []}
              netLoad={netLoadAgg?.data ?? []}
              buildingforecast={buildingForecastAgg?.data ?? []}
              summerChart={priceAgg?.data ?? []}
              smartmeterData={smartMeterAgg?.data?.timeSeries ?? []}
              scheduledCharging={sumChargeAgg?.data?.scheduledCharging ?? []}
              scheduledDischarging={sumChargeAgg?.data?.scheduledDischarging ?? []}
              chargerBreakdownSeries={chargerBreakdownSeries}
            />
          )}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ width: '100%', flexWrap: 'nowrap', alignItems: 'stretch' }}>
          {/* If data exists (even if refreshing), show the charts. Only show loader on absolute first fetch. */}
          {chargingInsightsAgg || analyticsStatus === 'succeeded' ? (
            <>
              {chargingInsightsAgg && (
                <Grid sx={getCustomGridSx('35%')}>
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <SessionsChart chargingiInsightData={chargingInsightsAgg} />
                  </Box>
                </Grid>
              )}
              <Grid sx={getCustomGridSx('30%')}>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <AppCurrentSubject
                    chartType="line"
                    charges={sumChargeAgg?.data?.chargeResult?.charges?.ByDay}
                    discharges={sumChargeAgg?.data?.chargeResult?.discharges?.ByDay}
                  />
                </Box>
              </Grid>
              <Grid sx={getCustomGridSx('35%')}>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <PageViewsBarChart
                    charges={smartMeterAgg?.data?.ByDay}
                    discharges={sumChargeAgg?.data?.chargeResult?.discharges?.ByDay}
                    title={`Discharges/Grid - ${config.lastDays} days`}
                  />
                </Box>
              </Grid>
            </>
          ) : (
             analyticsStatus === 'loading' && <Grid size={12} sx={{ py: 8 }}>
              {/* <LoadingComponent /> */}
              </Grid>
          )}
        </Grid>

        <Grid size={12} sx={{ mt: 2 }}>
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>Strategy Evaluation</Typography>
          <StrategyEvaluationPanel />
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}